import { useState, type ReactNode } from 'react';
import type { OrderRecord } from '@adiox/api';
import { formatServerDateTime, useDisplayZone } from '@adiox/ui';
import { BottomSheet } from '../mobile/BottomSheet';
import type { AccountFigures, Position } from '../stream/accountModel';
import { useWorkspace } from '../workspace/workspaceStore';
import { pendingDistance } from './tradeMath';

const tone = (v: number | null) => (v === null ? '' : v > 0 ? 'text-up' : v < 0 ? 'text-down' : '');
const kindOf = (type: string) => type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

const sheetAction =
  'block min-h-14 w-full border-t border-line px-4 py-4 text-center text-lg hover:bg-panel2 disabled:opacity-40';

/** A "label: value" pair of the details part of the sheet. */
function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span>
      <span className="text-muted">{label}: </span>
      <span className="num">{children}</span>
    </span>
  );
}

/** A New Order from the sheet: the ticket opens on the symbol of the line that was tapped. */
const newOrderFor = (symbol: string) => {
  useWorkspace.getState().select(symbol);
  useWorkspace.getState().actions?.orderTicket();
};

/**
 * The Trade screen on a phone, laid out as MetaTrader's phone terminal does it: the floating profit in large type, the
 * account figures in two columns, then a "Positions" band with one line per position (what it is and where it stands on the
 * left, its profit on the right) and an "Orders" band for pending orders. A tap on a line opens its actions.
 */
export function MobileTradeView({
  positions,
  orders,
  quotes,
  digitsOf,
  currency,
  figures,
  format,
  online,
  busy,
  onModify,
  onClose,
  onModifyOrder,
  onCancelOrder,
}: {
  positions: Position[];
  orders: OrderRecord[];
  quotes: Record<string, { bid: number; ask: number } | undefined>;
  digitsOf: (symbol: string) => number;
  currency: string;
  figures: AccountFigures;
  format: (v: number) => string;
  online: boolean;
  busy: boolean;
  onModify: (p: Position) => void;
  onClose: (p: Position) => void;
  onModifyOrder: (o: OrderRecord) => void;
  onCancelOrder: (o: OrderRecord) => void;
}) {
  const [picked, setPicked] = useState<{ kind: 'position' | 'order'; ticket: number } | null>(null);
  const zone = useDisplayZone();
  // Looked up live, so the sheet keeps following the prices, and closes itself when the position or order is gone.
  const position =
    picked?.kind === 'position' ? positions.find((p) => p.ticket === picked.ticket) : undefined;
  const order = picked?.kind === 'order' ? orders.find((o) => o.ticket === picked.ticket) : undefined;
  const dismiss = () => setPicked(null);
  const f = figures;
  const cells: [string, string][] = [
    ['Balance:', format(f.balance)],
    ['Free margin:', format(f.freeMargin)],
    ['Equity:', format(f.equity)],
    ['Level:', f.margin === 0 ? '0.00%' : `${format(f.marginLevel)}%`],
    ['Margin:', format(f.margin)],
  ];
  const band = 'bg-panel2 px-4 py-2 text-sm text-muted';
  const line = 'flex w-full items-center justify-between gap-3 border-b border-line px-4 py-3 text-left';

  return (
    <div className="flex flex-col">
      <p
        className={`num border-b border-line py-4 text-center text-2xl font-semibold ${tone(f.floating)}`}
        data-testid="total-profit"
      >
        {format(f.floating)} {currency}
      </p>

      <dl className="grid grid-cols-[auto_1fr_auto_1fr] items-baseline gap-x-3 gap-y-2 px-4 py-3 text-base">
        {cells.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted">{label}</dt>
            <dd className="num">{value}</dd>
          </div>
        ))}
      </dl>

      {positions.length > 0 && <h3 className={band}>Positions</h3>}
      <ul aria-label="Open positions">
        {positions.map((p) => {
          const d = digitsOf(p.symbol);
          return (
            <li key={p.ticket} data-testid="position-row" data-ticket={p.ticket}>
              <button
                type="button"
                className={line}
                aria-haspopup="dialog"
                onClick={() => setPicked({ kind: 'position', ticket: p.ticket })}
              >
                <span className="min-w-0">
                  <span className="block truncate">
                    <span className="font-semibold">{p.symbol}</span>{' '}
                    <span className={p.side === 'Buy' ? 'text-up' : 'text-down'}>
                      {p.side.toLowerCase()} {p.volume}
                    </span>
                  </span>
                  <span className="num block text-sm text-muted">
                    {p.priceOpen.toFixed(d)} → {p.price === null ? '—' : p.price.toFixed(d)}
                  </span>
                </span>
                <span className={`num text-lg font-semibold ${tone(p.profit)}`} data-testid="position-profit">
                  {p.profit === null ? '—' : format(p.profit)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {orders.length > 0 && <h3 className={band}>Orders</h3>}
      <ul aria-label="Pending orders">
        {orders.map((o) => {
          const d = digitsOf(o.symbol);
          const distance = pendingDistance(o, quotes[o.symbol], d);
          return (
            <li key={o.ticket} data-testid="order-row" data-ticket={o.ticket}>
              <button
                type="button"
                className={line}
                aria-haspopup="dialog"
                onClick={() => setPicked({ kind: 'order', ticket: o.ticket })}
              >
                <span className="min-w-0">
                  <span className="block truncate">
                    <span className="font-semibold">{o.symbol}</span>{' '}
                    <span className="text-muted">
                      {kindOf(o.type)} {o.volumeCurrent}
                    </span>
                  </span>
                  <span className="num block text-sm text-muted">
                    {distance === null ? '' : `${distance} points away`}
                  </span>
                </span>
                <span className="num text-lg">{o.priceOrder.toFixed(d)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <BottomSheet open={position !== undefined} label="Position" onClose={dismiss}>
        {position && (
          <div data-testid="position-sheet">
            <div className="flex flex-col gap-2 px-4 py-4">
              <div className="flex justify-between text-muted">
                <span className="num">{formatServerDateTime(position.timeOpen, zone.offsetAt)}</span>
                <span className="num">{position.ticket}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-lg">
                  <span className="font-semibold">{position.symbol}</span>{' '}
                  <span className={position.side === 'Buy' ? 'text-up' : 'text-down'}>
                    {position.side.toLowerCase()}
                  </span>{' '}
                  <span className="num text-accent">{position.volume}</span>
                </span>
                <span className={`num text-xl font-semibold ${tone(position.profit)}`}>
                  {position.profit === null ? '—' : format(position.profit)}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="num text-lg">
                  {position.priceOpen.toFixed(digitsOf(position.symbol))} →{' '}
                  {position.price === null ? '—' : position.price.toFixed(digitsOf(position.symbol))}
                </span>
                <span className="num text-accent">
                  {position.price === null
                    ? ''
                    : `${(((position.price - position.priceOpen) / position.priceOpen) * 100).toFixed(2)}%`}
                </span>
              </div>
              <div className="flex justify-between">
                <Detail label="S / L">
                  {position.stopLoss > 0 ? position.stopLoss.toFixed(digitsOf(position.symbol)) : '-'}
                </Detail>
                <Detail label="Swap">{Number(position.swap.toFixed(2))}</Detail>
              </div>
              <div className="flex justify-between">
                <Detail label="T / P">
                  {position.takeProfit > 0 ? position.takeProfit.toFixed(digitsOf(position.symbol)) : '-'}
                </Detail>
                {position.trailingPoints > 0 && (
                  <Detail label="Trailing">{position.trailingPoints} pts</Detail>
                )}
              </div>
            </div>
            <button
              type="button"
              className={`${sheetAction} text-down`}
              disabled={!online || busy}
              aria-label={`Close position ${position.ticket}`}
              onClick={() => {
                onClose(position);
                dismiss();
              }}
            >
              Close Position
            </button>
            <button
              type="button"
              className={sheetAction}
              onClick={() => {
                dismiss();
                newOrderFor(position.symbol);
              }}
            >
              New Order
            </button>
            <button
              type="button"
              className={sheetAction}
              aria-label={`Modify position ${position.ticket}`}
              onClick={() => {
                dismiss();
                onModify(position);
              }}
            >
              Modify Position
            </button>
          </div>
        )}
      </BottomSheet>

      <BottomSheet open={order !== undefined} label="Order" onClose={dismiss}>
        {order && (
          <div data-testid="order-sheet">
            <div className="flex flex-col gap-2 px-4 py-4">
              <div className="flex justify-between text-muted">
                <span className="num">{formatServerDateTime(order.timeSetup, zone.offsetAt)}</span>
                <span className="num">{order.ticket}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-lg">
                  <span className="font-semibold">{order.symbol}</span>{' '}
                  <span className="text-muted">{kindOf(order.type)}</span>{' '}
                  <span className="num text-accent">{order.volumeCurrent}</span>
                </span>
                <span className="num text-xl">{order.priceOrder.toFixed(digitsOf(order.symbol))}</span>
              </div>
              <div className="flex justify-between">
                <Detail label="S / L">
                  {order.stopLoss > 0 ? order.stopLoss.toFixed(digitsOf(order.symbol)) : '-'}
                </Detail>
                <Detail label="Away">
                  {(() => {
                    const away = pendingDistance(order, quotes[order.symbol], digitsOf(order.symbol));
                    return away === null ? '-' : `${away} pts`;
                  })()}
                </Detail>
              </div>
              <div className="flex justify-between">
                <Detail label="T / P">
                  {order.takeProfit > 0 ? order.takeProfit.toFixed(digitsOf(order.symbol)) : '-'}
                </Detail>
              </div>
            </div>
            <button
              type="button"
              className={`${sheetAction} text-down`}
              disabled={!online || busy}
              aria-label={`Cancel order ${order.ticket}`}
              onClick={() => {
                onCancelOrder(order);
                dismiss();
              }}
            >
              Delete Order
            </button>
            <button
              type="button"
              className={sheetAction}
              onClick={() => {
                dismiss();
                newOrderFor(order.symbol);
              }}
            >
              New Order
            </button>
            <button
              type="button"
              className={sheetAction}
              aria-label={`Modify order ${order.ticket}`}
              onClick={() => {
                dismiss();
                onModifyOrder(order);
              }}
            >
              Modify Order
            </button>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}
